import type { BaseEvent } from '@amplitude/analytics-types';
import { LogLevel } from '@amplitude/analytics-types';
import { omitBy } from 'lodash';

import config from 'js/app/config';
import logger from 'js/app/loggerSingleton';
import appInfo from 'js/lib/appInfo';
import * as cookie from 'js/lib/cookie';
import { get as getUser } from 'js/lib/user';

import type { Events as ClientEvents } from '@coursera/event-pulse/core';
import type { EventData, Events } from '@coursera/event-pulse/sdk';
import { client, track } from '@coursera/event-pulse/sdk';

import { GUIDES_AND_SURVEYS_ENABLED_APPS } from './guidesAndSurveysApps';

// This is a public API key (not a secret key). Safe to use in client-side code.
const API_KEY = '9f370ddd4287ab67b08ab58273ddf40c';

/**
 * Supported Amplitude Guides and Surveys events: 18 of the 20 events the SDK emits are supported,
 * i.e. they are defined in the eventing-schema and forwarded through the eventing pipeline.
 *
 * The two Booted events ([Guides-Surveys] Booted and [Guides-Surveys] Engagement Booted) are
 * intentionally excluded - they are only useful for troubleshooting, not analytics, and are
 * high-volume (~15M events per month).
 */
const GUIDES_AND_SURVEYS_EVENT_TYPES = {
  '[Guides-Surveys] Error': 'guides_surveys_error',
  '[Guides-Surveys] Error Pin Target Not Found': 'guides_surveys_error_pin_target_not_found',
  '[Guides-Surveys] Guide Abandoned': 'guides_surveys_guide_abandoned',
  '[Guides-Surveys] Guide Checklist Expand State Changed': 'guides_surveys_guide_checklist_expand_state_changed',
  '[Guides-Surveys] Guide Completed': 'guides_surveys_guide_completed',
  '[Guides-Surveys] Guide Dismissed': 'guides_surveys_guide_dismissed',
  '[Guides-Surveys] Guide Engaged': 'guides_surveys_guide_engaged',
  '[Guides-Surveys] Guide Rage Closed': 'guides_surveys_guide_rage_closed',
  '[Guides-Surveys] Guide Step Completed': 'guides_surveys_guide_step_completed',
  '[Guides-Surveys] Guide Viewed': 'guides_surveys_guide_viewed',
  '[Guides-Surveys] Survey Abandoned': 'guides_surveys_survey_abandoned',
  '[Guides-Surveys] Survey Completed': 'guides_surveys_survey_completed',
  '[Guides-Surveys] Survey Dismissed': 'guides_surveys_survey_dismissed',
  '[Guides-Surveys] Survey Engaged': 'guides_surveys_survey_engaged',
  '[Guides-Surveys] Survey Rage Closed': 'guides_surveys_survey_rage_closed',
  '[Guides-Surveys] Survey Step Completed': 'guides_surveys_survey_step_completed',
  '[Guides-Surveys] Survey Submitted': 'guides_surveys_survey_submitted',
  '[Guides-Surveys] Survey Viewed': 'guides_surveys_survey_viewed',
} satisfies Record<string, keyof Events>;

type GuideSurveyEventType = keyof typeof GUIDES_AND_SURVEYS_EVENT_TYPES;
type GuideSurveyEventName = (typeof GUIDES_AND_SURVEYS_EVENT_TYPES)[GuideSurveyEventType];

const SURVEY_RESPONSE_PROPERTY_NAME = '[Guides-Surveys] Survey Response';

/**
 * Prepare Amplitude Engagement event properties for the event-pulse pipeline.
 * - Excludes null values as event-pulse rejects them.
 * - Coerces the Survey Response property to a string for survey submitted events, as its type is
 *   fixed in eventing-schema but the SDK can emit it as an integer too.
 */
function transformEventProperties(
  eventType: GuideSurveyEventName,
  eventProperties: BaseEvent['event_properties']
): Record<string, unknown> {
  const filteredEventProperties = omitBy(eventProperties, (value) => value === null);
  if (
    eventType === 'guides_surveys_survey_submitted' &&
    filteredEventProperties[SURVEY_RESPONSE_PROPERTY_NAME] !== undefined
  ) {
    filteredEventProperties[SURVEY_RESPONSE_PROPERTY_NAME] = String(
      filteredEventProperties[SURVEY_RESPONSE_PROPERTY_NAME]
    );
  }
  return filteredEventProperties;
}

let eventForwardingRegistered = false;

/**
 * Forward tracked event-pulse events into the Engagement SDK so guides and surveys that use the
 * "On event tracked" trigger can fire.
 *
 * The engagement SDK doesn't send these events to Amplitude; no network call is made.
 * The SDK uses them only for local trigger evaluation.
 * https://amplitude.com/docs/sdks/guides-and-surveys/sdk#forwarding-events
 */
function registerEventForwarding(): void {
  if (eventForwardingRegistered) {
    return;
  }
  eventForwardingRegistered = true;

  const forwardEvent = (eventName: keyof ClientEvents, data: ClientEvents[keyof ClientEvents]) => {
    try {
      // eslint-disable-next-line camelcase -- Amplitude SDK event shape
      window.engagement?.forwardEvent({ event_type: eventName, event_properties: data });
    } catch (error) {
      logger.warn('Failed to forward event to Amplitude Engagement SDK:', error);
    }
  };

  // Forward lower-priority events (client from event-pulse SDK tracks lower-priority events).
  // Page views (highPriorityClient) events are not forwarded - Basic page view triggers can be setup in the Amplitude dashboard itself.
  client.on('beforeSend', forwardEvent);
}

/**
 * Initialize Amplitude Engagement SDK for Guides and Surveys.
 * Call init and boot to initialize Guides and Surveys and connect it to event-pulse to track the events.
 *
 * Initialization is async and non-blocking - failures are handled to prevent breaking the app.
 *
 * @returns Promise resolving to void
 */
async function initializeEngagement(): Promise<void> {
  try {
    // Get user and device ID.
    const user = getUser();
    const deviceId = cookie.get('__204u') ?? '';
    if (!deviceId || !user?.id) {
      return;
    }

    // Load the engagement SDK and initialize it
    const { init: engagementInit } = await import('@amplitude/engagement-browser');
    engagementInit(API_KEY, {
      serverZone: 'US',
      options: {
        logLevel: LogLevel.Error,
      },
    });
    if (!window.engagement || !window.engagement.boot) {
      return;
    }

    // Boot the engagement SDK
    await window.engagement.boot({
      user: {
        // eslint-disable-next-line camelcase
        user_id: String(user.id),
        // eslint-disable-next-line camelcase
        device_id: deviceId,
        // eslint-disable-next-line camelcase
        user_properties: {},
      },
      integrations: [
        {
          track: (event) => {
            // Only track supported guides and surveys events.
            const eventType = GUIDES_AND_SURVEYS_EVENT_TYPES[event.event_type as GuideSurveyEventType];
            if (!eventType) {
              return;
            }
            const filteredEventProperties = transformEventProperties(eventType, event.event_properties);
            if (config.environment === 'development') {
              logger.info('Amplitude Engagement SDK track event:', event);
            } else {
              track(eventType, filteredEventProperties as EventData<GuideSurveyEventName>).catch(logger.warn);
            }
          },
        },
      ],
    });

    // Forward tracked events so guides and surveys with an "On event tracked" trigger can fire.
    registerEventForwarding();
  } catch (error) {
    logger.error('Failed to initialize Amplitude Engagement SDK:', error);
  }
}

/**
 * Initializes Amplitude Engagement SDK for Guides and Surveys;
 * if the current app is in the allowlist (see guidesAndSurveysApps).
 */
const setupAmplitudeGuidesAndSurveys = (): undefined | Promise<void> => {
  const appName = appInfo.get().appName;
  if (!appName || !GUIDES_AND_SURVEYS_ENABLED_APPS.has(appName)) {
    return undefined;
  }
  return initializeEngagement().catch(logger.warn);
};

export default setupAmplitudeGuidesAndSurveys;
